import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Users, FileText, Package, Building2, UserCog, History } from "lucide-react";

const ICONS = { Customer: Users, Bill: FileText, LR: Package, Vendor: Building2, Staff: UserCog };
const RECENT_KEY = "ntc_recent_search";
const getRecents = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [recents, setRecents] = useState(getRecents);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const loadData = () => {
    if (data) return;
    Promise.all([api.get("/customers"), api.get("/invoices"), api.get("/shipments"), api.get("/vendors"), api.get("/staff-master")])
      .then(([c, i, s, v, st]) => setData({ customers: c.data || [], invoices: i.data || [], shipments: s.data || [], vendors: v.data || [], staff: st.data || [] }))
      .catch(() => setData({ customers: [], invoices: [], shipments: [], vendors: [], staff: [] }));
  };

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, []);

  const term = q.trim().toLowerCase();
  const has = (...vals) => vals.some((v) => String(v || "").toLowerCase().includes(term));
  const results = term.length >= 2 && data
    ? [
        ...data.customers.filter((c) => has(c.name, c.customer_code, c.phone)).slice(0, 3).map((c) => ({
          key: `c-${c.id}`, kind: "Customer", label: c.name, sub: [c.customer_code, c.phone].filter(Boolean).join(" · "), path: `/customer-ledger?customer=${c.id}`,
        })),
        ...data.invoices.filter((i) => has(i.invoice_no, i.customer_name)).slice(0, 3).map((i) => ({
          key: `i-${i.id}`, kind: "Bill", label: String(i.invoice_no || "").replace(/^INV[-\s]?/i, "Bill "), sub: `${i.customer_name || ""} · ${i.issue_date || ""}`, path: "/bills",
        })),
        ...data.shipments.filter((s) => has(s.lr_no, s.consignment_no, s.customer_name, s.consignor, s.consignee, s.invoice_no, s.dc_no, s.coa_no, ...(s.extra_invoices || []).map((x) => x.no), ...(s.extra_dcs || []).map((x) => x.no))).slice(0, 3).map((s) => {
          const lr = String(s.lr_no || s.consignment_no || "").replace(/^CN[-\s]?/i, "");
          return { key: `s-${s.id}`, kind: "LR", label: `LR ${lr}`, sub: `${s.origin || ""} → ${s.destination || ""}${s.status ? ` · ${s.status}` : ""}${s.coa_no ? ` · ${s.coa_no}` : ""}`, path: `/consignment-enquiry?q=${encodeURIComponent(lr)}` };
        }),
        ...data.vendors.filter((v) => has(v.name, v.phone)).slice(0, 3).map((v) => ({
          key: `v-${v.id}`, kind: "Vendor", label: v.name, sub: v.phone || v.type || "", path: "/vendor-ledger",
        })),
        ...data.staff.filter((s) => has(s.name, s.staff_code, s.phone)).slice(0, 3).map((s) => ({
          key: `st-${s.id}`, kind: "Staff", label: s.name, sub: `${s.designation || ""}${s.branch ? ` · ${s.branch}` : ""}`, path: "/staff-master",
        })),
      ].slice(0, 10)
    : [];

  const pick = (r) => {
    const next = [{ key: r.key, kind: r.kind, label: r.label, sub: r.sub, path: r.path }, ...recents.filter((x) => x.key !== r.key)].slice(0, 6);
    setRecents(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    setOpen(false);
    setQ("");
    navigate(r.path);
  };

  const showRecents = term.length < 2 && recents.length > 0;
  const list = showRecents ? recents : results;

  return (
    <div ref={boxRef} className="relative ml-auto w-full max-w-[340px]">
      <div className="flex items-center gap-2 rounded-md border border-border bg-white px-2.5 py-1.5">
        <Search className="h-4 w-4 shrink-0 text-slate-400" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { loadData(); setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); if (e.key === "Enter" && list[0]) pick(list[0]); }}
          placeholder="Search customer, bill, LR, vendor, staff…"
          className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
          data-testid="global-search-input"
        />
        <span className="hidden shrink-0 rounded border border-border px-1 py-0.5 text-[10px] font-semibold text-slate-400 md:block">Ctrl K</span>
      </div>
      {open && (showRecents || term.length >= 2) && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[340px] overflow-y-auto rounded-md border border-border bg-white py-1 shadow-lg" data-testid="global-search-results">
          {showRecents && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400" data-testid="recent-searches-label">
              <History className="h-3 w-3" /> Recently viewed
            </div>
          )}
          {!showRecents && !data && <div className="px-3 py-2 text-xs text-muted-foreground">Loading…</div>}
          {!showRecents && data && results.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No matches for “{q}”.</div>}
          {list.map((r) => {
            const Icon = ICONS[r.kind] || Search;
            return (
              <button
                key={r.key}
                type="button"
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() => pick(r)}
                data-testid={showRecents ? "recent-search-item" : "global-search-result"}
              >
                <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{r.label}</span>
                  <span className="block truncate text-xs text-slate-500">{r.sub}</span>
                </span>
                <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{r.kind}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
